import React, { useEffect, useState } from 'react';
import { CalendarClock, Loader2, X } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { PersonalSkill } from '../../types/aai';

/**
 * F1. The way a routine gets made.
 *
 * There was exactly one path before this: accept a tool-scout suggestion whose
 * kind came back as "routine". That needs a connected tool, spends tokens, and
 * the kind is not guaranteed, so in practice most people had no way to schedule
 * anything. A feature you can only reach by luck is not shipped.
 *
 * This is the same three-shape schedule as the accept dialog, in one component
 * with two entry points: a skill row ("Run on a schedule") and the Routines
 * screen ("Schedule a skill"). Whichever you use, the routine lands in Settings
 * and the scheduler picks it up on its next tick.
 *
 * Whatever the person picks is read in THEIR zone, which is why the browser's
 * zone is sent with it. Nine o'clock means nine o'clock where they are.
 */

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export interface ScheduleSkillDialogProps {
  /** Pre-selected skill. Omit to let the person pick from their own skills. */
  skill?: { id: string; name: string };
  onClose: () => void;
  onSaved?: () => void;
}

const ScheduleSkillDialog: React.FC<ScheduleSkillDialogProps> = ({ skill, onClose, onSaved }) => {
  const [skills, setSkills] = useState<PersonalSkill[] | null>(skill ? [] : null);
  const [skillId, setSkillId] = useState(skill?.id || '');
  const [cadence, setCadence] = useState('weekdays');
  const [weekday, setWeekday] = useState(0);
  // A morning briefing is the shape people actually want, so the common case is
  // one click.
  const [time, setTime] = useState('09:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skill) return;
    aaiApi.getMySkills()
      .then((r) => setSkills(r.data.skills || []))
      .catch(() => setSkills([]));
  }, [skill]);

  const save = async () => {
    if (!skillId) { setError('Pick a skill to run.'); return; }
    setBusy(true);
    setError(null);
    try {
      await aaiApi.createRoutine({
        skill_id: skillId,
        cadence,
        time_of_day: time,
        weekday: cadence === 'weekly' ? weekday : undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      onSaved?.();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not set that schedule.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="w-[min(460px,100%)] rounded-panel bg-white p-5 shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
        <div className="mb-1 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand-600">
              <CalendarClock className="h-4 w-4" />
            </span>
            <h2 className="text-base font-semibold text-ink">Run this on a schedule</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-ink-soft hover:text-ink-muted disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-4 text-[12.5px] text-ink-dim">
          It runs as you, through the tools you have connected, and never does anything your
          delegation answers say it may not.
        </p>

        {!skill && (
          <>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-dim">
              Which skill
            </label>
            {skills === null ? (
              <div className="mb-3 flex items-center gap-2 text-[12.5px] text-ink-soft">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your skills...
              </div>
            ) : skills.length === 0 ? (
              <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                You have no skills yet. Build one in the skill builder first, then come back and give
                it a time.
              </p>
            ) : (
              <select value={skillId} onChange={(e) => setSkillId(e.target.value)}
                className="mb-3 w-full rounded-lg border border-line px-3 py-2 text-sm">
                <option value="">Pick a skill</option>
                {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </>
        )}

        {skill && (
          <p className="mb-3 rounded-lg bg-canvas px-3 py-2 text-[13px] font-medium text-ink">
            {skill.name}
          </p>
        )}

        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-dim">
          When
        </label>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select value={cadence} onChange={(e) => setCadence(e.target.value)}
            className="rounded-lg border border-line px-2.5 py-2 text-sm">
            <option value="daily">Every day</option>
            <option value="weekdays">Every weekday</option>
            <option value="weekly">Every week</option>
          </select>
          {cadence === 'weekly' && (
            <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}
              className="rounded-lg border border-line px-2.5 py-2 text-sm">
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          )}
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="rounded-lg border border-line px-2.5 py-2 text-sm" />
        </div>
        <p className="mb-4 text-[11.5px] text-ink-soft">
          Times are in your own timezone. You can pause it or read what it produced in Settings,
          under Routines.
        </p>

        {error && <p className="mb-3 text-[12px] text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="rounded-lg bg-canvas-inset px-3 py-1.5 text-[12px] font-semibold text-ink hover:bg-line disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={busy || !skillId}
            className="rounded-lg bg-brand-500 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
            {busy ? 'Saving...' : 'Schedule it'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSkillDialog;
